import React, {
  forwardRef,
  Ref,
  useCallback,
  useEffect,
  useImperativeHandle,
  useState,
} from 'react';
import { FaPencilAlt, FaTrashAlt } from 'react-icons/fa';
import { format, parseISO } from 'date-fns';
import { useToast } from '../../../hooks/toast';
import api from '../../../services/api';
import Button from '../../../components/Button';
import Loading from '../../../components/Loading';
import Paginacao from '../../../components/Paginacao';

import Modal from '../../../components/Modal';

import { IFormBusca } from '../index';
import ServicoCadastro from '../ServicoCadastro';
import getResponseErrors from '../../../utils/getResponseErrors';

export interface IPaginacao {
  termoBusca: IFormBusca;
  limite: number;
  pagina: number;
}

export interface IServicoNFDTO {
  SerCodigo: number;
  SerPerCodigo: number;
  SerUsuCodigo: number;
  SerCodigoServico: string;
  SerDescricao: string;
  SerCNAE: string;
  SerAliquota: string;
  SerCodigoTributacao: string;
  SerTipoTributacao: number;
  SerCodigoNBS: string;
  SerIndicOp: string;
  SerIbsClassTrib: string;
  SerIbsSituTrib: string;
  created_at: Date;
  updated_at: Date;
  created_atFormatado: string;
  updated_atFormatado: string;
  usuario: {
    UsuCodigo: number;
    UsuNome: string;
  };
  perfil: {
    PerCodigo: number;
    PerNome: string;
  };
}

export type ICreateServicoNFDTO = Omit<
  IServicoNFDTO,
  'SerCodigo' | 'created_at' | 'updated_at' | 'perfil' | 'usuario'
>;

export interface IDadosLista {
  perfil?: string;
}

export interface IRetornoListagemServico {
  data: IServicoNFDTO[];
  count: number;
}
export interface RefObject {
  addRegistro(
    dadosServicoCadsatro: IServicoNFDTO,
    dadosLista: IDadosLista,
    conCodigo: number | undefined,
  ): Promise<void>;
}
type Props = {
  dados?: IFormBusca;
  ref: Ref<RefObject>;
};

export function formataServicoNF(servico: IServicoNFDTO): IServicoNFDTO {
  const formatado = {
    ...servico,
    created_atFormatado: format(
      parseISO(servico.created_at.toString()),
      'dd/MM/yyyy HH:mm:ss',
    ),
    updated_atFormatado: format(
      parseISO(servico.updated_at.toString()),
      'dd/MM/yyyy HH:mm:ss',
    ),
  };
  return formatado;
}

const ListaServicos: React.FC<Props> = forwardRef(
  (prop: Props, ref: Ref<RefObject>) => {
    const [showModalCadastro, setShowModalCadastro] = useState(false);
    const { addToast } = useToast();
    const [paginacao, setPaginacao] = useState<IPaginacao | undefined>(
      undefined,
    );
    const [loading, setLoading] = useState(false);
    const [totalRegistros, setTotalRegistros] = useState<number | undefined>(
      undefined,
    );
    const [listagemServicos, setListagemServicos] = useState<IServicoNFDTO[]>(
      [],
    );

    const [servicoSelecionado, setServicoSelecionado] = useState<
      number | undefined
    >(undefined);

    const [labels, setLabels] = useState<IDadosLista | undefined>(undefined);

    useEffect(() => {
      setPaginacao({
        termoBusca: prop.dados || {
          SerPerCodigo: '',
        },
        pagina: 1,
        limite: 20,
      });
    }, [prop.dados]);

    useEffect(() => {
      async function buscaServicos(): Promise<void> {
        if (paginacao) {
          try {
            setLoading(true);
            const { SerPerCodigo } = paginacao.termoBusca;
            let query = '';
            if (SerPerCodigo !== '')
              query = `${query}&SerPerCodigo=${SerPerCodigo}`;
            // if (descricao !== '') query = `${query}&SerDescricao=${descricao}`;
            // if (aliquota !== '') query = `${query}&SerAliquota=${aliquota}`;
            // if (nbs !== '') query = `${query}&SerCodigoNBS=${nbs}`;

            const response = await api.get<IRetornoListagemServico>(
              `/serviconf/busca?pagina=${paginacao.pagina}&limite=${paginacao.limite}${query}`,
            );

            const { data, count } = response.data;

            const listagemFormatada = data.map(lista => {
              return formataServicoNF(lista);
            });

            setTotalRegistros(count);

            setListagemServicos(listagemFormatada);
          } catch (error) {
            const mensagemErro = getResponseErrors(error);
            addToast({
              title: 'Não foi possível listar os serviços',
              type: 'error',
              description: mensagemErro,
            });
          } finally {
            setLoading(false);
          }
        }
      }
      if (paginacao) buscaServicos();
    }, [paginacao, addToast]);

    const handleDelete = useCallback(
      async (codigo: number) => {
        setLoading(true);
        try {
          await api.delete(`/serviconf/${codigo}`);
          setListagemServicos(State =>
            State?.filter(con => con.SerCodigo !== codigo),
          );
          addToast({
            type: 'success',
            title: 'Registro excluído',
          });
        } catch (error) {
          const mensagemErro = getResponseErrors(error);
          addToast({
            title: 'Não foi possível deletar o registro',
            type: 'error',
            description: mensagemErro,
          });
        } finally {
          setLoading(false);
        }
      },
      [addToast],
    );

    const addRegistro = useCallback(
      async (
        dadosServicoCadsatro: ICreateServicoNFDTO,
        dadosLista: IDadosLista,
        SerCodigo: number | undefined,
      ) => {
        let response;
        setLoading(true);
        try {
          if (SerCodigo !== undefined) {
            response = await api.put<IServicoNFDTO>(
              `/serviconf/${SerCodigo}`,
              dadosServicoCadsatro,
            );
          } else {
            response = await api.post<IServicoNFDTO>(
              '/serviconf',
              dadosServicoCadsatro,
            );
          }

          const retornoServicoNF: IServicoNFDTO = formataServicoNF(
            response.data,
          );

          setListagemServicos(state => {
            return state
              ? [retornoServicoNF].concat(
                  state.filter(
                    config => config.SerCodigo !== retornoServicoNF.SerCodigo,
                  ),
                )
              : [retornoServicoNF];
          });
          setShowModalCadastro(false);
          addToast({ title: 'Servico salvo', type: 'success' });
        } catch (error) {
          const mensagemErro = getResponseErrors(error);
          addToast({
            title: 'Não foi possível salvar o serviço',
            type: 'error',
            description: mensagemErro,
            timeAlive: 10000,
          });
        } finally {
          setLoading(false);
        }
      },
      [addToast],
    );

    useImperativeHandle(ref, () => ({ addRegistro }));

    const handleProximaPagina = useCallback((page: number) => {
      setPaginacao(state => {
        return state && { ...state, pagina: page };
      });
    }, []);

    const handleEditar = useCallback(
      (serCodigo: number, nomeLabels: IDadosLista) => {
        setServicoSelecionado(serCodigo);
        setShowModalCadastro(true);
        setLabels(nomeLabels);
      },
      [],
    );

    return (
      <>
        {showModalCadastro && (
          <Modal
            titulo="Cadastro de Serviço Nota Fiscal"
            handleFecharModal={() => setShowModalCadastro(false)}
            large
            show={showModalCadastro}
          >
            <ServicoCadastro
              serCodigo={servicoSelecionado}
              addRegistro={addRegistro}
              labels={labels}
            />
          </Modal>
        )}
        {loading && <Loading />}

        {listagemServicos ? (
          <>
            <table
              className="table table-hover table-sm table-font-small"
              style={{ borderWidth: '0px' }}
            >
              <tbody>
                <tr className="bg-light" style={{ fontSize: 'Small' }}>
                  <th scope="col">Perfil</th>
                  <th scope="col">Descricao</th>
                  <th scope="col">Aliquota</th>
                  <th scope="col">Cod. Serviço</th>
                  <th scope="col">CNAE</th>
                  <th scope="col">Tipo Tributação</th>
                  <th scope="col">Cod. Tributação</th>
                  <th scope="col">Indica Op.</th>
                  <th scope="col">NBS</th>
                  <th scope="col">IBS Class</th>
                  <th scope="col">IBS Substituição</th>
                  <th scope="col">&nbsp;</th>
                  <th scope="col">&nbsp;</th>
                </tr>
                {listagemServicos.length > 0 &&
                  listagemServicos.map((lista: IServicoNFDTO) => (
                    <tr
                      key={lista.SerCodigo}
                      style={{ fontSize: 'Small', height: '30px' }}
                    >
                      <td>{lista.perfil.PerNome}</td>
                      <td>{lista.SerDescricao}</td>
                      <td>{lista.SerAliquota}</td>
                      <td style={{ width: '70px' }}>
                        {lista.SerCodigoServico}
                      </td>
                      <td>{lista.SerCNAE}</td>
                      <td style={{ width: '70px' }}>
                        {lista.SerTipoTributacao}
                      </td>
                      <td style={{ width: '60px' }}>
                        {lista.SerCodigoTributacao}
                      </td>
                      <td style={{ width: '60px' }}>{lista.SerIndicOp}</td>
                      <td style={{ width: '60px' }}>{lista.SerCodigoNBS}</td>
                      <td style={{ width: '60px' }}>{lista.SerIbsClassTrib}</td>
                      <td style={{ width: '60px' }}>{lista.SerIbsSituTrib}</td>
                      <td className="p-0" style={{ width: '40px' }}>
                        <Button
                          type="button"
                          variant="primary-outline"
                          className="py-0"
                          title={`${lista.SerCodigo}`}
                          onClick={
                            () =>
                              handleEditar(lista.SerCodigo, {
                                perfil: lista.perfil.PerNome,
                              })
                            // eslint-disable-next-line react/jsx-curly-newline
                          }
                        >
                          <FaPencilAlt />
                        </Button>
                      </td>

                      <td className="p-0" style={{ width: '40px' }}>
                        {lista.SerCodigo > 0 && (
                          <Button
                            type="button"
                            variant="secondary-outline"
                            className="py-0"
                            title={`${lista.SerCodigo}`}
                            onClick={() => {
                              handleDelete(lista.SerCodigo);
                            }}
                          >
                            <FaTrashAlt />
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
            {listagemServicos.length > 0 && (
              <Paginacao
                limitePagina={paginacao?.limite || 20}
                paginasVizinhas={2}
                totalRegistros={totalRegistros}
                paginaInicio={paginacao?.pagina}
                handleProximaPagina={handleProximaPagina}
              />
            )}
          </>
        ) : (
          <div
            className="d-flex justify-content-center align-items-center"
            style={{ minHeight: '250px' }}
          >
            <h5>
              Pesquise os serviços cadastrados para emissão de Notas Fiscais
            </h5>
          </div>
        )}
        {listagemServicos && listagemServicos.length === 0 && (
          <div
            className="d-flex justify-content-center align-items-center"
            style={{ minHeight: '250px' }}
          >
            <h5>Nenhum serviço encontrado com os parametros buscados</h5>
          </div>
        )}
      </>
    );
  },
);

export default ListaServicos;
